import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../utils/axios.ts';

export interface PaymentPriority {
  type: 'fines' | 'loans' | 'contributions' | 'drives';
  name: string;
  priority: number;
  order?: number;
  enabled: boolean;
  color: 'red' | 'orange' | 'green' | 'blue';
}

export interface MemberTarget {
  target_amount: number;
  from_date: string;
  to_date: string;
}

export interface PaymentRoutingSettings {
  payment_priorities: PaymentPriority[];
  contribution_cycle: 'daily' | 'weekly' | 'monthly';
  monthly_contribution_day?: number | 'last';
  weekly_contribution_day?: 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';
  daily_contribution_time?: string;
  contribution_amount: number; // Kept for backward compatibility, but not used in UI
  tracking_start_date?: string | null;
  member_targets?: Record<number, number | MemberTarget> | null; // Can be number (old) or MemberTarget (new)
  subgroup_targets?: Record<number, number | { target_amount: number; from_date: string; to_date: string }> | null;
  allow_partial_contributions: boolean;
  auto_fines_enabled: boolean;
  grace_period_days: number;
  fine_amount: number;
  max_fines_per_month: number;
  auto_deduct_fines: boolean;
  auto_deduct_loans: boolean;
  notify_admin_on_payment: boolean;
  notify_member_on_payment: boolean;
  due_reminders_enabled?: boolean;
  late_reminders_enabled?: boolean;
  reminder_days_before_due?: number;
  late_reminder_frequency_days?: number;
  drive_priority_available?: boolean;
}

export const usePaymentRouting = () => {
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['payment-routing-settings'],
    queryFn: async () => {
      const response = await api.get('/payment-routing/settings');
      const raw = response.data?.data;
      // API may return a single object or an array (collection)
      const settings = Array.isArray(raw) ? (raw[0] || {}) : (raw || {});

      const drivePriorityAvailable = Boolean(settings.drive_priority_available);
      let rawPriorities = settings.payment_priorities ? [...settings.payment_priorities] : [
        { type: 'fines', name: 'Fines', enabled: true, priority: 1, order: 1, color: 'red' },
        { type: 'loans', name: 'Loans', enabled: true, priority: 2, order: 2, color: 'orange' },
        { type: 'contributions', name: 'Contributions', enabled: true, priority: 3, order: 3, color: 'green' },
      ];
      if (drivePriorityAvailable && !rawPriorities.some((p: any) => p.type === 'drives')) {
        rawPriorities.push({
          type: 'drives',
          name: 'Drives',
          enabled: false,
          color: 'blue',
          priority: rawPriorities.length + 1,
          order: rawPriorities.length + 1,
        });
      }

      const priorities: PaymentPriority[] = rawPriorities
        .map((p: any, idx: number) => {
          const num = Number(p.priority ?? p.order ?? idx + 1);
          return {
            ...p,
            priority: num,
            order: num,
          };
        })
        .sort((a, b) => a.priority - b.priority);

      // Normalize defaults so form controls are controlled
      const normalized: PaymentRoutingSettings = {
        payment_priorities: priorities,
        drive_priority_available: drivePriorityAvailable,
        contribution_cycle: settings.contribution_cycle || 'monthly',
        monthly_contribution_day: ((): number => {
          const src = settings.monthly_contribution_day;
          if (src === 'last') return 31;
          const n = Number(src ?? 1);
          return Number.isFinite(n) && n >= 1 ? n : 1;
        })(),
        weekly_contribution_day: settings.weekly_contribution_day || 'monday',
        daily_contribution_time: settings.daily_contribution_time || '09:00',
        contribution_amount: Number(settings.contribution_amount ?? 0),
        tracking_start_date: settings.tracking_start_date || null,
        member_targets: settings.member_targets || null,
        subgroup_targets: settings.subgroup_targets || null,
        allow_partial_contributions: Boolean(settings.allow_partial_contributions),
        auto_fines_enabled: Boolean(settings.auto_fines_enabled),
        grace_period_days: Number(settings.grace_period_days ?? 0),
        fine_amount: Number(settings.fine_amount ?? 0),
        max_fines_per_month: Number(settings.max_fines_per_month ?? 0),
        auto_deduct_fines: Boolean(settings.auto_deduct_fines),
        auto_deduct_loans: Boolean(settings.auto_deduct_loans),
        notify_admin_on_payment: Boolean(settings.notify_admin_on_payment),
        notify_member_on_payment: Boolean(settings.notify_member_on_payment),
        due_reminders_enabled: Boolean(settings.due_reminders_enabled ?? true),
        late_reminders_enabled: Boolean(settings.late_reminders_enabled ?? true),
        reminder_days_before_due: Number(settings.reminder_days_before_due ?? 1),
        late_reminder_frequency_days: Number(settings.late_reminder_frequency_days ?? 3),
        // Note: due_reminder_message and late_reminder_message are now stored in sms_templates table
      } as PaymentRoutingSettings;

      // eslint-disable-next-line no-console
      console.log('[PaymentRouting] GET /payment-routing/settings normalized', normalized);
      return normalized;
    },
  });

  const updatePaymentRoutingSettings = useMutation({
    mutationFn: async (settings: PaymentRoutingSettings) => {
      // Map flat UI settings to backend schema expected by validator
      let sorted = [...(settings.payment_priorities || [])]
        .sort((a, b) => Number(a.priority ?? (a as any).order ?? 999) - Number(b.priority ?? (b as any).order ?? 999));
      // Backend requires at least one enabled priority with sequential order; fallback if empty
      if (!sorted.length) {
        sorted = [
          { type: 'contributions', name: 'Contributions', enabled: true, priority: 1, order: 1, color: 'green' },
          { type: 'fines', name: 'Fines', enabled: false, priority: 2, order: 2, color: 'red' },
          { type: 'loans', name: 'Loans', enabled: false, priority: 3, order: 3, color: 'orange' },
        ];
      }
      const payment_priorities = sorted.map((p, idx) => ({
        type: p.type,
        name: p.name,
        enabled: !!p.enabled,
        color: p.color || (p.type === 'fines' ? 'red' : p.type === 'loans' ? 'orange' : p.type === 'drives' ? 'blue' : 'green'),
        priority: idx + 1,
        order: idx + 1,
      }));

      // Validator requires integer monthly_contribution_day always and valid time format
      const weeklyDay = settings.weekly_contribution_day || 'monday';
      const monthlyDay = ((): number => {
        const src = settings.monthly_contribution_day;
        if (src === 'last') return 31;
        const n = Number(src ?? 1);
        return Number.isFinite(n) && n >= 1 ? n : 1;
      })();

      const autoFinesEnabled = Boolean(settings.auto_fines_enabled);
      
      // Build auto_fines object - only include required fields when enabled
      const auto_fines: any = {
        enabled: autoFinesEnabled,
      };
      
      // Only include grace_period_days, fine_amount, and max_fines_per_month if auto_fines is enabled
      // Otherwise, validation will fail because these fields have min:1 requirement
      if (autoFinesEnabled) {
        auto_fines.grace_period_days = Math.max(1, Math.min(30, Math.floor(Number(settings.grace_period_days ?? 7) || 7)));
        auto_fines.fine_amount = Math.max(0, Number(settings.fine_amount ?? 0));
        auto_fines.max_fines_per_month = Math.max(1, Math.min(10, Math.floor(Number(settings.max_fines_per_month ?? 3) || 3)));
      }
      
      const contributionCycle = settings.contribution_cycle || 'monthly';
      
      // Build contribution_settings - only include cycle-specific fields
      const contribution_settings: any = {
        cycle: contributionCycle,
        contribution_amount: Number(settings.contribution_amount ?? 0),
        tracking_start_date: settings.tracking_start_date || null,
        member_targets: settings.member_targets || null,
        subgroup_targets: settings.subgroup_targets || null,
        allow_partial_contributions: Boolean(settings.allow_partial_contributions),
        auto_fines,
      };
      
      // Only include cycle-specific fields based on the selected cycle
      if (contributionCycle === 'monthly') {
        contribution_settings.monthly_contribution_day = monthlyDay;
      } else if (contributionCycle === 'weekly') {
        contribution_settings.weekly_contribution_day = weeklyDay;
      } else if (contributionCycle === 'daily') {
        // Format time as HH:MM (e.g., "09:00" not "9:00")
        const timeValue = settings.daily_contribution_time || '09:00';
        // Ensure proper format: split by :, pad hours to 2 digits
        const [hours, minutes = '00'] = timeValue.split(':');
        const formattedTime = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
        contribution_settings.daily_contribution_time = formattedTime;
      }

      const fine_settings = { auto_deduct: Boolean(settings.auto_deduct_fines) };
      const loan_settings = { auto_deduct: Boolean(settings.auto_deduct_loans) };
      
      // Build notification settings
      // Always include reminder fields with valid defaults to preserve settings even when disabled
      // Clamp values to valid ranges to ensure they pass validation
      const reminderDaysBeforeDue = Math.max(0, Math.min(30, Math.floor(Number(settings.reminder_days_before_due ?? 1) || 1)));
      const lateReminderFrequency = Math.max(1, Math.min(30, Math.floor(Number(settings.late_reminder_frequency_days ?? 3) || 3)));
      
      const notification_settings = {
        notify_admin_on_payment: Boolean(settings.notify_admin_on_payment),
        notify_member_on_payment: Boolean(settings.notify_member_on_payment),
        due_reminders_enabled: Boolean(settings.due_reminders_enabled ?? true),
        late_reminders_enabled: Boolean(settings.late_reminders_enabled ?? true),
        reminder_days_before_due: reminderDaysBeforeDue,
        late_reminder_frequency_days: lateReminderFrequency,
      };
      // Note: due_reminder_message and late_reminder_message are now stored in sms_templates table

      const payload = {
        payment_priorities,
        contribution_settings,
        fine_settings,
        loan_settings,
        notification_settings,
      } as any;

      // Helpful console for debugging in the browser
      // eslint-disable-next-line no-console
      console.log('[PaymentRouting] PUT /payment-routing/settings payload', payload);

      const response = await api.put('/payment-routing/settings', payload).catch((err) => {
        // eslint-disable-next-line no-console
        console.error('[PaymentRouting] PUT error', err?.response?.status, err?.response?.data);
        throw err;
      });
      // eslint-disable-next-line no-console
      console.log('[PaymentRouting] Response', response?.data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-routing-settings'] });
    },
  });

  const resetPaymentRoutingSettings = useMutation({
    mutationFn: async () => {
      const response = await api.post('/payment-routing/settings/reset');
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-routing-settings'] });
      queryClient.invalidateQueries({ queryKey: ['can-calculate-balance'] });
    },
  });

  return {
    data,
    isLoading,
    error,
    updatePaymentRoutingSettings,
    resetPaymentRoutingSettings,
  };
}; 